// ProjectsTable.tsx — Sprint 12. Column defs + table card. The
// columns inline progress bars (budget consumption) and call back
// into the orchestrator's view/edit/delete handlers via props.
import React from 'react';
import { Button, Card, Progress, Space, Table, Tooltip } from 'antd';
import {
  CalendarOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined,
  EyeOutlined, UserOutlined,
} from '@ant-design/icons';
import type { Project } from './projectTypes';
import { calculateBudgetProgress, getPriorityTag, getStatusTag } from './projectRules';

import { formatDate } from '../../utils/date';
export interface ProjectsTableProps {
  rows:             Project[];
  canManage:        boolean;
  onView:           (project: Project) => void;
  onEdit:           (project: Project) => void;
  onDelete:         (id: string) => void;
}

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  rows, canManage, onView, onEdit, onDelete,
}) => {
  const columns = [
    {
      title: 'Project Name', dataIndex: 'name', key: 'name',
      render: (text: string, record: Project) => (
        <Space direction="vertical" size="small">
          <span className="font-medium">{text}</span>
          <small className="text-gray-500">{record.category}</small>
        </Space>
      ),
    },
    { title: 'Status',   dataIndex: 'status',   key: 'status',   render: getStatusTag },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', render: getPriorityTag },
    {
      title: 'Budget', key: 'budget',
      render: (_: any, r: Project) => (
        <Space direction="vertical" size="small">
          <span className="font-medium">${r.budget.toLocaleString()}</span>
          {r.actualCost && (
            <Progress
              percent={calculateBudgetProgress(r.budget, r.actualCost)}
              size="small"
              status={r.actualCost > r.budget ? 'exception' : 'active'}
              format={(percent) => `${percent?.toFixed(0)}%`}
            />
          )}
        </Space>
      ),
    },
    {
      title: 'Responsible', dataIndex: 'responsiblePerson', key: 'responsiblePerson',
      render: (text: string) => (
        <Space><UserOutlined /><span>{text}</span></Space>
      ),
    },
    {
      title: 'Timeline', key: 'timeline',
      render: (_: any, r: Project) => (
        <Space direction="vertical" size="small">
          <small><CalendarOutlined /> Start: {formatDate(r.startDate)}</small>
          <small><CheckCircleOutlined /> End: {formatDate(r.endDate)}</small>
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: Project) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => onView(r)} />
          </Tooltip>
          {canManage && (
            <>
              <Tooltip title="Edit">
                <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
              </Tooltip>
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(r.id)} />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />
    </Card>
  );
};
